import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link as RouterLink,
  useLocation,
} from 'react-router-dom';
import { AppBar, Toolbar, Typography, Container, Link, Box, IconButton, Menu, MenuItem, useMediaQuery, useTheme, Button, ThemeProvider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import logo from './assets/images/Supervoima-Logo-White.svg';
import theme from './theme';

import Login from './components/Login';
import AccountSettings from './components/AccountSettings';
import Interview from './components/Interview';
import Results from './components/Results';
import AdminDashboard from './components/AdminDashboard';
import DemoBanner from './components/DemoBanner';
import DemoSessionFeedback from './components/DemoSessionFeedback';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import LoadingIndicator from './components/LoadingIndicator';

const Navigation = ({ isAuthenticated, isAdmin, logout, showDemoBanner }) => {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: '#021c2d', borderBottom: '4px solid #FFD700', top: showDemoBanner ? '48px' : 0, transition: 'top 0.3s ease' }}>
        <Toolbar>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <img 
              src={logo} 
              alt="Supervoima Logo" 
              style={{ 
                height: '48px', 
                width: 'auto'
              }} 
            />
          </Box>
          
          {/* Title - responsive */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <Link component={RouterLink} to="/" color="inherit" underline="none">
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Bebas Neue', 
                  fontWeight: 'normal',
        
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '2.5rem' }
                }}
              >
                SUPERVOIMA AI EVALUATOR
              </Typography>
            </Link>
          </Box>
          
          {/* Desktop Navigation */}
          {isAuthenticated && !isMobile && (
            <>
              <Link
                component={RouterLink}
                to="/interview"
                color="inherit"
                underline="hover"
                sx={{ ml: 2, fontSize: '0.9rem' }}
              >
                Assessment
              </Link>
              <Link
                component={RouterLink}
                to="/results"
                color="inherit"
                underline="hover"
                sx={{ ml: 2, fontSize: '0.9rem' }}
              >
                Reports
              </Link>
              <Link
                component={RouterLink}
                to="/account"
                color="inherit"
                underline="hover"
                sx={{ ml: 2, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <SettingsIcon sx={{ fontSize: '1rem' }} />
                Account
              </Link>
              {isAdmin && (
                <Link
                  component={RouterLink}
                  to="/admin"
                  color="inherit"
                  underline="hover"
                  sx={{ ml: 2, fontSize: '0.9rem' }}
                >
                  Admin
                </Link>
              )}

              <Button
                startIcon={<LogoutIcon />}
                onClick={logout}
                color="inherit"
                sx={{
                  ml: 2,
                  fontSize: '0.9rem',
                  textTransform: 'none'
                }}
              >
                Logout
              </Button>
            </>
          )}
          
          {/* Mobile Hamburger Menu */}
          {isAuthenticated && isMobile && (
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#021c2d',
              color: 'white',
              minWidth: 150
            }
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            handleMobileMenuClose();
            window.location.href = '/interview';
          }}
          sx={{ color: 'white' }}
        >
          Interview
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMobileMenuClose();
            window.location.href = '/results';
          }}
          sx={{ color: 'white' }}
        >
          Results
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMobileMenuClose();
            window.location.href = '/account';
          }}
          sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <SettingsIcon sx={{ fontSize: '1rem' }} />
          Account
        </MenuItem>
        {isAdmin && (
          <MenuItem
            onClick={() => {
              handleMobileMenuClose();
              window.location.href = '/admin';
            }}
            sx={{ color: 'white' }}
          >
            Admin
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleMobileMenuClose();
            logout();
          }}
          sx={{ color: 'white' }}
        >
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag && window.location.hostname === 'evaluator.supervoima.ai') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);

  return null;
};

const MainContent = () => {
  const { isAuthenticated, isDemoMode, showDemoFeedback, closeDemoFeedback, logout, requestDemoLogout } = useAuth();
  const { isAdmin } = useAdmin();

  // Show demo banner when authenticated and in demo mode
  const showDemoBanner = isDemoMode && isAuthenticated;

  // Use demo feedback flow for logout when in demo mode
  const handleLogout = isDemoMode ? requestDemoLogout : logout;

  return (
    <>
      {showDemoBanner && <DemoBanner />}
      <DemoSessionFeedback open={showDemoFeedback} onClose={closeDemoFeedback} />
      <Navigation isAuthenticated={isAuthenticated} isAdmin={isAdmin} logout={handleLogout} showDemoBanner={showDemoBanner} />
      <Container sx={{
        mt: showDemoBanner ? { xs: 14, md: 14 } : { xs: 8, md: 12 },
        mb: { xs: 2, md: 4 }
      }}>
        <Routes>
          <Route path="/" element={isAuthenticated ? <Interview /> : <Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account" element={<AccountSettings />} />
          <Route path="/interview" element={<Interview />} />
          <Route path="/results" element={<Results />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </Container>
    </>
  );
};

const AppContent = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ height: '100vh', width: '100vw' }}>
        <LoadingIndicator />
      </Box>
    );
  }

  return (
    <Router>
      <RouteTracker />
      <MainContent />
    </Router>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <AdminProvider>
          <AppContent />
        </AdminProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;